import { useState } from 'react';
import { HeroStatTile } from '../components/HeroStatTile';
import { SkelBar, SkelBadge, SkeletonPreviewLabel } from '../components/Skeleton';

/**
 * Dormant Accounts visual shell. Admin-only. Ports v0.7 DormantPage
 * (index.html line ~4935).
 *
 * 7.1d update: Adds account-row skeletons mirroring v0.7's dormant
 * account display:
 *   - Company name + last touch date
 *   - Assigned owner (Avatar + name)
 *   - Signal count badge
 *   - Days-since indicator (color-coded)
 *   - Suggested action button (Run Dormant Check / Claim / Reassign)
 */
export function Dormant() {
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('All');

  const dormantSkeletons = [
    { coW: 132, ownerW: 96, lastTouchW: 88, daysAgo: 142, signalCount: 3, tone: 'amber' },
    { coW: 108, ownerW: 112, lastTouchW: 76, daysAgo: 268, signalCount: 0, tone: 'red' },
    { coW: 156, ownerW: 84, lastTouchW: 92, daysAgo: 96, signalCount: 5, tone: 'amber' },
    { coW: 124, ownerW: 108, lastTouchW: 84, daysAgo: 412, signalCount: 1, tone: 'red' },
    { coW: 96, ownerW: 124, lastTouchW: 72, daysAgo: 188, signalCount: 2, tone: 'amber' },
  ];

  function daysToneClass(tone: string): string {
    if (tone === 'red') return 'bdse-dormant-days bdse-dormant-days-red';
    if (tone === 'amber') return 'bdse-dormant-days bdse-dormant-days-amber';
    return 'bdse-dormant-days';
  }

  return (
    <div>
      <div className="bdse-hero">
        <div className="bdse-hero-row">
          <div>
            <h1 className="bdse-hero-title">Dormant Accounts</h1>
            <p className="bdse-hero-subtitle">
              Accounts with elapsed activity windows
            </p>
          </div>
          <div className="bdse-hero-actions">
            <button
              type="button"
              disabled
              title="Coming in Stream 3D"
              className="bdse-hero-btn-ghost"
            >
              Upload Dormant List
            </button>
            <button
              type="button"
              disabled
              title="Coming in Stream 3D"
              className="bdse-hero-btn-ghost"
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="bdse-hero-stats">
          <HeroStatTile label="Dormant" value="0" sub="accounts" icon="users" />
          <HeroStatTile label="90+ days" value="0" sub="no activity" icon="clock" />
          <HeroStatTile label="180+ days" value="0" sub="long dormant" icon="clock" />
          <HeroStatTile label="Claimed" value="0" sub="in re-engagement" icon="check" />
        </div>
      </div>

      <div className="bdse-search-row">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search dormant accounts by company or owner..."
          className="bdse-search-input"
          aria-label="Search dormant accounts"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="bdse-search-clear"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      <div className="bdse-filter-bar bdse-filter-bar-spaced">
        {['All', '90+ days', '180+ days', '365+ days'].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setTierFilter(f)}
            className={`bdse-filter-btn ${tierFilter === f ? 'bdse-filter-btn-active' : ''}`}
          >
            {f} (0)
          </button>
        ))}
      </div>

      <SkeletonPreviewLabel text="Sample preview - dormant accounts crossing the threshold surface here" />

      <div className="bdse-dormant-list">
        {dormantSkeletons.map((d, i) => (
          <article key={i} className="bdse-dormant-row bdse-skeleton-card">
            <div className="bdse-dormant-row-co">
              <SkelBar w={d.coW} h={12} />
              <div className="bdse-dormant-row-last">
                <span className="bdse-dormant-row-last-label">Last touch</span>
                <SkelBar w={d.lastTouchW} h={9} />
              </div>
            </div>
            <div className="bdse-dormant-row-owner">
              <span className="bdse-dormant-row-owner-label">Owner</span>
              <SkelBar w={d.ownerW} h={10} />
            </div>
            <div className="bdse-dormant-row-signals">
              <SkelBadge w={56} h={18} />
              <span className="bdse-dormant-row-signals-label">
                {d.signalCount} signal{d.signalCount === 1 ? '' : 's'}
              </span>
            </div>
            <div className={daysToneClass(d.tone)}>{d.daysAgo}d</div>
            <div className="bdse-dormant-row-actions">
              <button
                type="button"
                disabled
                title="Coming in Stream 3D"
                className="bdse-btn-secondary-small"
              >
                Run Check
              </button>
              <button
                type="button"
                disabled
                title="Coming in Stream 3D"
                className="bdse-btn-secondary-small"
              >
                Claim
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
